import {
  Grid,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import InputMask from "react-input-mask";
import React from "react";
import api from "../../services/api";

export default function AddressForm() {
  const [numcep, setCep] = React.useState(sessionStorage.getItem("cep") || '');
  const [numero, setNumero] = React.useState(sessionStorage.getItem("numero") || '');
  const [telefone, setTelefone] = React.useState(
    sessionStorage.getItem("telefone")
    || '');

  const [errorCep, setErrorCep] = React.useState(null);
  const [loadingCep, setLoadingCep] = React.useState(false);
  const [UseEndereco, setUseEndereco] = React.useState({
    rua: sessionStorage.getItem("rua") || '',
    bairro: sessionStorage.getItem("bairro") || '',
    municipio: sessionStorage.getItem("municipio") || '',
    estado: sessionStorage.getItem("estado") || '',
  });
  
  // Refs para controlar debounce e cancelar requisições
  const debounceTimerRef = React.useRef(null);
  const abortControllerRef = React.useRef(null);
  const lastCepConsultedRef = React.useRef(null);

  const handleNumero = (event) => {
    const {
      target: { value },
    } = event;
    setNumero(value);
    if (value.length > 0) sessionStorage.setItem("numero", value);
    else {
      sessionStorage.removeItem("numero");
    }
  };

  const handleTelefone = (event) => {
    const {
      target: { value },
    } = event;
    setTelefone(value);
    if (value.length > 0) sessionStorage.setItem("telefone", value);
    else {
      sessionStorage.removeItem("telefone");
    }
  };

  const handleAddressField = (event, field) => {
    const {
      target: { value },
    } = event;

    const currentAddress = { ...UseEndereco };
    currentAddress[field] = value;
    setUseEndereco(currentAddress);

    if (value.length > 0) sessionStorage.setItem(field, value);
    else {
      sessionStorage.removeItem(field);
    }
  };

  const consultarCep = React.useCallback((cepLimpo) => {
    // Evita consultar o mesmo CEP novamente
    if (lastCepConsultedRef.current === cepLimpo) {
      return;
    }

    // Cancela requisição anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Cria novo AbortController
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoadingCep(true);
    setErrorCep(null);

    api
      .get(`cep/${cepLimpo}`, { signal })
      .then((response) => {
        // Ignora se a requisição foi cancelada
        if (signal.aborted) {
          return;
        }

        const data = response.data;
        const rua = data.street || '';
        const bairro = data.neighborhood || '';
        const municipio = data.city || '';
        const estado = data.state || '';
        const cep = data.cep || '';
        
        setUseEndereco({
          rua,
          bairro,
          municipio,
          cep,
          estado,
        });
        sessionStorage.setItem("cep", cep);
        sessionStorage.setItem("estado", estado);
        sessionStorage.setItem("municipio", municipio);
        sessionStorage.setItem("bairro", bairro);
        sessionStorage.setItem("rua", rua);
        
        lastCepConsultedRef.current = cepLimpo;
        setLoadingCep(false);
      })
      .catch((error) => {
        // Ignora se a requisição foi cancelada
        if (signal.aborted || error.name === 'AbortError') {
          return;
        }

        setLoadingCep(false);
        
        // Tratamento de erros específicos
        if (error.response) {
          const status = error.response.status;
          if (status === 404) {
            setErrorCep({ numcep: "CEP não encontrado." });
          } else if (status === 429) {
            setErrorCep({ numcep: "Muitas requisições. Aguarde um momento." });
          } else if (status === 502) {
            setErrorCep({ numcep: "Falha ao consultar CEP. Tente novamente." });
          } else {
            setErrorCep({ numcep: "Não conseguimos resgatar o seu CEP." });
          }
        } else {
          setErrorCep({ numcep: "Erro ao consultar CEP. Verifique sua conexão." });
        }

        // Limpa campos em caso de erro
        setUseEndereco({
          rua: "",
          bairro: "",
          municipio: "",
          cep: "",
          estado: "",
        });
        sessionStorage.removeItem("cep");
        sessionStorage.removeItem("estado");
        sessionStorage.removeItem("municipio");
        sessionStorage.removeItem("bairro");
        sessionStorage.removeItem("rua");
      });
  }, []);

  const handleCep = (event) => {
    const {
      target: { value },
    } = event;
    setCep(value);
    setErrorCep(null);

    // Remove formatação para validar
    const cepLimpo = value.replace(/\D/g, '');

    // Limpa timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Valida se tem 8 dígitos
    if (cepLimpo.length === 8) {
      // Debounce de 450ms antes de consultar
      debounceTimerRef.current = setTimeout(() => {
        consultarCep(cepLimpo);
      }, 450);
    } else if (cepLimpo.length > 0) {
      // CEP incompleto - não mostra erro ainda, apenas limpa se tinha dados
      if (cepLimpo.length < 8) {
        // Limpa campos se o CEP foi alterado para um valor incompleto
        setUseEndereco({
          rua: "",
          bairro: "",
          municipio: "",
          cep: "",
          estado: "",
        });
        lastCepConsultedRef.current = null;
      }
    } else {
      // Campo vazio - limpa tudo
      setErrorCep({ numcep: "CEP inválido." });
      sessionStorage.removeItem("cep");
      sessionStorage.removeItem("estado");
      sessionStorage.removeItem("municipio");
      sessionStorage.removeItem("bairro");
      sessionStorage.removeItem("rua");
      lastCepConsultedRef.current = null;
    }
  };

  const handleCepBlur = (event) => {
    const {
      target: { value },
    } = event;
    const cepLimpo = value.replace(/\D/g, '');

    // Limpa timer de debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Se tem 8 dígitos e ainda não consultou, consulta imediatamente
    if (cepLimpo.length === 8 && lastCepConsultedRef.current !== cepLimpo) {
      consultarCep(cepLimpo);
    } else if (cepLimpo.length !== 8 && cepLimpo.length > 0) {
      setErrorCep({ numcep: "CEP inválido. Deve conter 8 dígitos." });
    }
  };

  // Cleanup ao desmontar componente
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <Grid container spacing={3} sx={{ my: 3 }}>
      <Grid item xs={12} sm={6}>
        <Box sx={{ position: 'relative' }}>
          <InputMask
            mask="99.999-999"
            value={numcep}
            disabled={loadingCep}
            maskChar=" "
            onBlur={handleCepBlur}
            onChange={handleCep}
          >
            {() => (
              <TextField
                color="secondary"
                id="cep"
                label="CEP"
                name="cep"
                autoComplete="cep"
                error={Boolean(errorCep?.numcep)}
                helperText={errorCep?.numcep || (loadingCep ? "Consultando CEP..." : "")}
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          </InputMask>
          {loadingCep && (
            <CircularProgress
              size={20}
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                marginTop: '-10px',
              }}
            />
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="rua"
          name="rua"
          label="Rua"
          fullWidth
          autoComplete="address"
          color="secondary"
          value={UseEndereco.rua}
          onChange={e => handleAddressField(e, 'rua')}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="numero"
          name="numero"
          label="Número"
          fullWidth
          autoComplete="number"
          color="secondary"
          value={numero}
          onChange={handleNumero}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="bairro"
          name="bairro"
          label="Bairro"
          fullWidth
          autoComplete="neighborhood"
          color="secondary"
          value={UseEndereco.bairro}
          onChange={e => handleAddressField(e, 'bairro')}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="municipio"
          name="municipio"
          label="Município"
          fullWidth
          autoComplete="city"
          color="secondary"
          value={UseEndereco.municipio}
          onChange={e => handleAddressField(e, 'municipio')}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputMask
          mask="(99) 9 9999 9999"
          value={telefone}
          disabled={false}
          maskChar=" "
          onChange={handleTelefone}
          onBlur={handleTelefone}
        >
          {() => (
            <TextField
              required
              id="telefone"
              name="telefone"
              label="Telefone"
              fullWidth
              autoComplete="phone"
              color="secondary"
              value={telefone}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        </InputMask>
      </Grid>
    </Grid>
  );
}
